import { Box } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { Stage, Layer, Rect, Group, Text, Image } from 'react-konva'
import { WorkArea } from '../../../types/Project/WorkArea'
import useImage from 'use-image'
import Konva from 'konva'
import { KonvaEventObject } from 'konva/lib/Node'

type WorkAreaSelectionModalProps = {
	workAreas: WorkArea[]
	imageUrl: string
	setSelected: (area: WorkArea) => void
}

export const AreaSelectCanvas = ({
	workAreas,
	imageUrl,
	setSelected,
}: WorkAreaSelectionModalProps) => {
	const [img, status] = useImage(imageUrl, 'anonymous')
	const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

	const imageWidth = img ? img.width : window.innerWidth
	const imageHeight = img ? img.height : window.innerHeight
	const containerRef = useRef<HTMLDivElement>(null)
	const groupRef = useRef<Konva.Group>(null)

	// State for pinch-zoom tracking
	const [lastCenter, setLastCenter] = useState<Konva.Vector2d | null>(null)
	const [lastDist, setLastDist] = useState(0)

	useEffect(() => {
		if (status === 'loaded' && containerRef.current) {
			const { clientWidth, clientHeight } = containerRef.current
			setDimensions({ width: clientWidth, height: clientHeight })
		}
	}, [status])

	function handleWheel(e: KonvaEventObject<WheelEvent>): void {
		e.evt.preventDefault()
		const group = groupRef.current
		if (!group) return
		const stage = group.getStage()
		if (!stage) return

		// Get the current group scale (assume uniform scale)
		const oldScale = group.scaleX()
		const pointer = stage.getPointerPosition()
		if (!pointer) return

		// Define a zoom factor: zoom in when deltaY < 0, zoom out otherwise.
		const scaleBy = 1.05
		const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy

		// Invert the group's absolute transform to compute pointer position relative to the group.
		const transform = group.getAbsoluteTransform().copy()
		transform.invert()
		const mousePointTo = transform.point(pointer)

		// Set the new scale on the group.
		group.scale({ x: newScale, y: newScale })

		// Compute new absolute position so that the pointer stays at the same relative spot inside the group.
		const newPos = {
			x: pointer.x - mousePointTo.x * newScale,
			y: pointer.y - mousePointTo.y * newScale,
		}

		group.position(newPos)
		group.getLayer()?.batchDraw()
	}

	// --- Touch Handlers for Pinch-Zoom ---
	function getDistance(p1: Konva.Vector2d, p2: Konva.Vector2d) {
		return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
	}

	function getCenter(p1: Konva.Vector2d, p2: Konva.Vector2d): Konva.Vector2d {
		return {
			x: (p1.x + p2.x) / 2,
			y: (p1.y + p2.y) / 2,
		}
	}

	function handleTouchStart(e: KonvaEventObject<TouchEvent>) {
		e.evt.preventDefault()
		const touch1 = e.evt.touches[0]
		const touch2 = e.evt.touches[1]
		const stage = e.target.getStage()

		if (touch1 && touch2 && stage) {
			// Disable drag during pinch
			if (groupRef.current) {
				groupRef.current.draggable(false)
			}

			const p1 = { x: touch1.clientX, y: touch1.clientY }
			const p2 = { x: touch2.clientX, y: touch2.clientY }

			setLastCenter(getCenter(p1, p2))
			setLastDist(getDistance(p1, p2))
		}
	}

	function handleTouchMove(e: KonvaEventObject<TouchEvent>) {
		e.evt.preventDefault()
		const touch1 = e.evt.touches[0]
		const touch2 = e.evt.touches[1]
		const stage = e.target.getStage()
		const group = groupRef.current

		if (touch1 && touch2 && stage && group && lastCenter) {
			const p1 = { x: touch1.clientX, y: touch1.clientY }
			const p2 = { x: touch2.clientX, y: touch2.clientY }

			const newCenter = getCenter(p1, p2)
			const newDist = getDistance(p1, p2)

			// Calculate scale
			const oldScale = group.scaleX()
			const newScale = oldScale * (newDist / lastDist)

			// Calculate new position
			const transform = group.getAbsoluteTransform().copy()
			transform.invert()
			const pointTo = transform.point(lastCenter)

			const newPos = {
				x: newCenter.x - pointTo.x * newScale,
				y: newCenter.y - pointTo.y * newScale,
			}

			// Apply scale and position
			group.scale({ x: newScale, y: newScale })
			group.position(newPos)

			// Update last state for next move
			setLastCenter(newCenter)
			setLastDist(newDist)
			group.getLayer()?.batchDraw()
		}
	}

	function handleTouchEnd(e: KonvaEventObject<TouchEvent>) {
		// Reset state and re-enable drag
		setLastCenter(null)
		setLastDist(0)
		if (groupRef.current) {
			groupRef.current.draggable(true)
		}
	}

	if (dimensions.width === 0 || dimensions.height === 0) {
		return (
			<div
				ref={containerRef}
				style={{
					width: '100%',
					height: '87vh',
					backgroundColor: '#F5F5F5',
				}}
			/>
		)
	}
	return (
		<div
			ref={containerRef}
			style={{
				width: '100%',
				height: '100%',
				position: 'relative',
				backgroundColor: '#F5F5F5',
			}}
		>
			<Stage
				width={dimensions.width}
				height={dimensions.height}
				onWheel={handleWheel}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
			>
				<Layer>
					<Rect x={0} y={0} width={dimensions.width} height={dimensions.height} fill='#F5F5F5' />
				</Layer>
				<Layer>
					<Group draggable width={imageWidth} height={imageHeight} ref={groupRef}>
						<Image image={img} />
						{workAreas
							.filter((wa) => wa.x)
							.map((workArea, idx) => {
								const left = Math.min(workArea.x!, workArea.x! + workArea.width!)
								const right = Math.max(workArea.x!, workArea.x! + workArea.width!)
								const top = Math.min(workArea.y!, workArea.y! + workArea.height!)
								const bottom = Math.max(workArea.y!, workArea.y! + workArea.height!)

								const centerX = (left + right) / 2
								const centerY = (top + bottom) / 2

								return (
									<React.Fragment key={idx}>
										<Rect
											x={workArea.x}
											y={workArea.y}
											width={workArea.width}
											height={workArea.height}
											strokeWidth={2}
											fill={workArea.color}
											opacity={0.6}
											dash={[2, 2]}
											onMouseDown={() => setSelected(workArea)}
											onTap={() => setSelected(workArea)}
										/>

										<Text
											text={workArea.name}
											x={centerX}
											y={centerY}
											fontSize={16}
											ref={(node) => {
												if (node) {
													node.offsetX(node.width() / 2)
													node.offsetY(node.height() / 2)
												}
											}}
										/>
									</React.Fragment>
								)
							})}
					</Group>
				</Layer>
			</Stage>
		</div>
	)
}
